import { useEffect, useState } from "react";
import type { PendingAppointmentDTO } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Button } from "@/components/ui/Button";

type StatusFilter = "" | "pending" | "confirmed" | "rejected";

/**
 * Pending appointments — minimal ops surface for bookings that ran
 * without a connected Google Calendar.
 */
export function PendingAppointmentsPage() {
  const [items, setItems] = useState<PendingAppointmentDTO[]>([]);
  const [status, setStatus] = useState<StatusFilter>("pending");
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.agentflow.listPendingAppointments(
        status === "" ? undefined : status,
      );
      setItems(res.pendingAppointments);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to load");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function transition(id: string, next: "confirmed" | "rejected") {
    try {
      const updated = await api.agentflow.transitionPendingAppointment(id, next);
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to update");
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Operations
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Pending appointments
        </h1>
        <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
          Bookings captured by the agent when Google Calendar wasn&rsquo;t connected. Confirm or
          reject manually.
        </p>
      </header>

      <select
        value={status}
        onChange={(e) => setStatus(e.target.value as StatusFilter)}
        className="h-9 rounded-md border border-zinc-200 bg-white px-3 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40"
      >
        <option value="">All</option>
        <option value="pending">Pending</option>
        <option value="confirmed">Confirmed</option>
        <option value="rejected">Rejected</option>
      </select>

      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {err}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800/80 dark:bg-zinc-900/40">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
            <tr>
              <th className="px-4 py-2.5">Code</th>
              <th className="px-4 py-2.5">Customer</th>
              <th className="px-4 py-2.5">Service</th>
              <th className="px-4 py-2.5">Preferred</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
            {items.map((p) => (
              <tr key={p.id}>
                <td className="px-4 py-3 font-mono text-[12.5px]">{p.confirmationCode}</td>
                <td className="px-4 py-3 text-[12.5px]">
                  <div className="font-mono">{p.customerPhone}</div>
                  {p.customerName && (
                    <div className="text-[11px] text-zinc-500">{p.customerName}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-[12.5px]">{p.serviceType}</td>
                <td className="px-4 py-3 text-[12.5px]">{p.preferredWindow}</td>
                <td className="px-4 py-3 text-[12.5px] uppercase">{p.status}</td>
                <td className="px-4 py-3 text-right">
                  {p.status === "pending" && (
                    <span className="inline-flex gap-2">
                      <Button size="sm" onClick={() => void transition(p.id, "confirmed")}>
                        Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost-danger"
                        onClick={() => void transition(p.id, "rejected")}
                      >
                        Reject
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && !busy && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[12.5px] text-zinc-500">
                  Nothing here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
